import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import "./CycleTrackerModal.css";

import iconArrowL from "@/assets/icons/Icon-Arrow.png";
import iconArrowR from "@/assets/icons/Icon-Arrow1.png";

// ✅ Import des hooks
import useAuthSession from "../../../../authPatient/hooks/useAuthSession";
import { useUserCycle } from "@/cycle/hooks/useUserCycle";

/* ----------------- SVG ICONS ----------------- */
const CycleSvg = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path d="M3 12a9 9 0 0 1 15.36-6.36" stroke="#FF6DA9" strokeWidth="1.6" strokeLinecap="round" />
    <path d="M21 12a9 9 0 0 1-15.36 6.36" stroke="#FF6DA9" strokeWidth="1.6" strokeLinecap="round" />
    <path d="M18 3v4h-4" stroke="#FF6DA9" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const DropSvg = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path
      d="M12 3C12 3 6 10 6 14a6 6 0 0 0 12 0c0-4-6-11-6-11Z"
      stroke="#FF6DA9"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

/* ----------------- Dates helpers ----------------- */
const pad2 = (n) => String(n).padStart(2, "0");

const startOfDay = (d) => {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
};

const addDays = (d, n) => {
  const x = new Date(d);
  x.setDate(x.getDate() + n);
  return x;
};

const ymdKey = (d) => `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
const mondayIndex = (jsDay) => (jsDay + 6) % 7;

/* ----------------- Month helpers ----------------- */
/* ========================================
   ✅ PREV + CURRENT MONTHS
======================================== */
// Helper: start of month
const startOfMonth = (d) => new Date(d.getFullYear(), d.getMonth(), 1);

function buildMonthGrid(viewDate) {
  const year = viewDate.getFullYear();
  const month = viewDate.getMonth();

  const firstOfMonth = new Date(year, month, 1);
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const offset = mondayIndex(firstOfMonth.getDay());

  const totalCells = offset + daysInMonth;
  const totalWeeks = Math.ceil(totalCells / 7);
  const cellsCount = totalWeeks * 7;

  const prevMonthLastDay = new Date(year, month, 0).getDate();
  const grid = [];

  for (let i = 0; i < cellsCount; i++) {
    const cellIndex = i - offset + 1;
    let cellDate;
    let currentMonthFlag = true;

    if (cellIndex < 1) {
      currentMonthFlag = false;
      const day = prevMonthLastDay + cellIndex;
      cellDate = new Date(year, month - 1, day);
    } else if (cellIndex > daysInMonth) {
      currentMonthFlag = false;
      const day = cellIndex - daysInMonth;
      cellDate = new Date(year, month + 1, day);
    } else {
      cellDate = new Date(year, month, cellIndex);
    }

    grid.push({ date: cellDate, day: cellDate.getDate(), currentMonth: currentMonthFlag, key: ymdKey(cellDate) });
  }

  const weeks = [];
  for (let w = 0; w < totalWeeks; w++) weeks.push(grid.slice(w * 7, w * 7 + 7));
  return weeks;
}

function makeRangeSet(from, to) {
  const set = new Set();
  if (!from || !to) return set;

  const a = startOfDay(from);
  const b = startOfDay(to);
  const start = a <= b ? a : b;
  const end = a <= b ? b : a;

  for (let d = new Date(start); d <= end; d = addDays(d, 1)) set.add(ymdKey(d));
  return set;
}

/* =========================
   Fertile + Peak calculation
========================= */
function computeFertileAndPeakSets({ lastPeriodStart, cycleLength, windowStart, windowEnd, periodSet }) {
  const fertSet = new Set();
  const peakSet = new Set();

  if (!lastPeriodStart) return { fertSet, peakSet };

  // If cycle length is not provided, do not compute predictions
  if (!cycleLength) return { fertSet, peakSet };

  const start = startOfDay(lastPeriodStart);
  const safeCycle = Math.max(15, Math.min(60, Number(cycleLength)));
  const maxCycles = 24;

  for (let c = 0; c < maxCycles; c++) {
    const cycleStart = addDays(start, c * safeCycle);
    if (cycleStart > windowEnd) break;

    const peak = addDays(cycleStart, safeCycle - 14);
    const peakKey = ymdKey(peak);

    const fertStart = addDays(peak, -5);
    const fertEnd = addDays(peak, 1);

    for (let d = startOfDay(fertStart); d <= fertEnd; d = addDays(d, 1)) {
      if (d >= windowStart && d <= windowEnd) fertSet.add(ymdKey(d));
    }

    if (peak >= windowStart && peak <= windowEnd) peakSet.add(peakKey);
  }

  if (periodSet) {
    for (const k of periodSet) {
      fertSet.delete(k);
      peakSet.delete(k);
    }
  }
  for (const k of peakSet) fertSet.delete(k);

  return { fertSet, peakSet };
}

function computePillEdges(weeks, fertSet, periodSet) {
  const meta = new Map();
  const typeOf = (key) => (periodSet.has(key) ? "per" : fertSet.has(key) ? "fert" : null);

  for (const week of weeks) {
    for (let i = 0; i < week.length; i++) {
      const key = week[i].key;
      const t = typeOf(key);
      if (!t) continue;

      const prev = i > 0 ? typeOf(week[i - 1].key) : null;
      const next = i < 6 ? typeOf(week[i + 1].key) : null;

      const isStart = prev !== t;
      const isEnd = next !== t;
      const isSingle = isStart && isEnd;

      meta.set(key, { type: t, isStart, isEnd, isSingle });
    }
  }
  return meta;
}

export default function CycleTrackerModal({
  open,
  onClose,
  onSave,
  initialValues,
  mode,
  nowOverride,
}) {
  const { t, i18n } = useTranslation("cycle");

  // ✅ Récupération de l'utilisateur connecté
  const { effectiveUserId } = useAuthSession({ auto: true, once: true });
  const userId = effectiveUserId;

  // ✅ Chargement des données du cycle depuis le backend
  const { 
    cycles, 
    summary, 
    predictions,
    fertilityWindow,
    isLoading, 
    error,
    createCycle,
    reloadAll 
  } = useUserCycle(userId, {
    autoFetch: true,
    withSummary: true,
    // Do not fetch predictions/fertility until we know the user has cycles
    withPredictions: false,
    withFertilityWindow: false,
  });

  // Fetch predictions/fertility only when backend has cycles (one-time)
  const [predictionsLoaded, setPredictionsLoaded] = useState(false);
  useEffect(() => {
    if (predictionsLoaded) return;
    if (cycles && cycles.length > 0) {
      // reloadAll will fetch predictions and fertility window via the hook loaders
      reloadAll();
      setPredictionsLoaded(true);
    }
  }, [cycles, predictionsLoaded, reloadAll]);

  // ✅ Calculer la durée des règles à partir des dates
  const calculatePeriodLength = (startDate, endDate) => {
    if (!startDate || !endDate) return null;
    const start = new Date(startDate);
    const end = new Date(endDate);
    const diffTime = Math.abs(end - start);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
    return Math.max(2, Math.min(14, diffDays));
  };

  // ✅ Déterminer les valeurs initiales depuis le backend ou initialValues
  const getInitialCycleData = () => {
    // Si on a des initialValues, les utiliser
    if (initialValues?.lastPeriodStart) {
      return {
        lastPeriodStart: initialValues.lastPeriodStart,
        cycleLength: initialValues.cycleLength ?? null,
        periodLength: initialValues.periodLength ?? null,
      };
    }

    // Sinon, utiliser les données du dernier cycle réel du backend
    if (summary?.lastRealCycle) {
      const lastCycle = summary.lastRealCycle;
      return {
        lastPeriodStart: lastCycle.startDate,
        cycleLength: summary.averageCycleLength ?? lastCycle.cycleLengthInDays ?? null,
        periodLength: calculatePeriodLength(lastCycle.startDate, lastCycle.endDate) ?? null,
      };
    }

    // Valeurs par défaut
    return {
      lastPeriodStart: null,
      cycleLength: null,
      periodLength: null,
    };
  };

  const isEdit = mode === "edit" || Boolean(initialValues?.lastPeriodStart);

  const today = useMemo(() => startOfDay(new Date()), []);
  const [viewDate, setViewDate] = useState(() => startOfMonth(today));
  const [periodStart, setPeriodStart] = useState(null);
  const [periodEnd, setPeriodEnd] = useState(null);
  const [cycleLength, setCycleLength] = useState(null);
  const [periodDuration, setPeriodDuration] = useState(null);
  const [isSaving, setIsSaving] = useState(false);

  /* ----------------- Hydratation des données ----------------- */
  useEffect(() => {
    if (!open) return;

    const initialData = getInitialCycleData();

    setCycleLength(initialData.cycleLength);
    setPeriodDuration(initialData.periodLength);

    if (isEdit && initialData.lastPeriodStart) {
      const savedStart = new Date(initialData.lastPeriodStart);
      const normalizedStart = startOfDay(savedStart);

      setPeriodStart(normalizedStart);
      setPeriodEnd(addDays(normalizedStart, (initialData.periodLength ?? 5) - 1));

      const savedMonth = new Date(savedStart.getFullYear(), savedStart.getMonth(), 1);
      setViewDate(savedMonth);
      return;
    }

    setPeriodStart(null);
    setPeriodEnd(null);
    setViewDate(startOfMonth(today));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, isEdit, today]);

  // ESC + scroll lock
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => e.key === "Escape" && onClose?.();
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [open, onClose]);

  const weekdays = useMemo(
    () => [
      t("cycleTracker.weekdays.mon"),
      t("cycleTracker.weekdays.tue"),
      t("cycleTracker.weekdays.wed"),
      t("cycleTracker.weekdays.thu"),
      t("cycleTracker.weekdays.fri"),
      t("cycleTracker.weekdays.sat"),
      t("cycleTracker.weekdays.sun"),
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [i18n.language]
  );

  const weeks = useMemo(() => buildMonthGrid(viewDate), [viewDate]);

  const { windowStart, windowEnd } = useMemo(() => {
    const first = weeks?.[0]?.[0]?.date || new Date(viewDate.getFullYear(), viewDate.getMonth(), 1);
    const last = weeks?.[weeks.length - 1]?.[6]?.date || new Date(viewDate.getFullYear(), viewDate.getMonth() + 1, 0);
    return { windowStart: startOfDay(first), windowEnd: startOfDay(last) };
  }, [weeks, viewDate]);

  // ✅ NOUVELLE LOGIQUE : Combiner cycles locaux + backend
  const { periodSet, allFertSet, allPeakSet } = useMemo(() => {
    // 1. Période en cours de sélection (locale)
    const localPeriodSet = makeRangeSet(periodStart, periodEnd);

    // 2. Sets pour tous les cycles du backend
    const backendPeriodSet = new Set();
    const backendFertSet = new Set();
    const backendPeakSet = new Set();

    // Ajouter les cycles réels du backend
    if (cycles?.length > 0) {
      cycles.forEach(cycle => {
        if (!cycle.isPredicted) {
          const start = startOfDay(new Date(cycle.startDate));
          const end = startOfDay(new Date(cycle.endDate));
          
          for (let d = new Date(start); d <= end; d = addDays(d, 1)) {
            backendPeriodSet.add(ymdKey(d));
          }
        }
      });
    }

    // Ajouter les prédictions du backend
    if (predictions?.length > 0) {
      predictions.forEach(pred => {
        // Période prédite
        const predStart = startOfDay(new Date(pred.predictedStartDate));
        const predEnd = startOfDay(new Date(pred.predictedEndDate || pred.endDate));
        
        for (let d = new Date(predStart); d <= predEnd; d = addDays(d, 1)) {
          backendPeriodSet.add(ymdKey(d));
        }

        // Fenêtre fertile
        if (pred.fertileWindow) {
          const fertStart = startOfDay(new Date(pred.fertileWindow.start));
          const fertEnd = startOfDay(new Date(pred.fertileWindow.end));
          
          for (let d = new Date(fertStart); d <= fertEnd; d = addDays(d, 1)) {
            const key = ymdKey(d);
            if (!backendPeriodSet.has(key)) {
              backendFertSet.add(key);
            }
          }
        }

        // Jour d'ovulation (peak)
        if (pred.ovulationDate) {
          const ovDate = startOfDay(new Date(pred.ovulationDate));
          const ovKey = ymdKey(ovDate);
          if (!backendPeriodSet.has(ovKey)) {
            backendPeakSet.add(ovKey);
            backendFertSet.delete(ovKey); // Le peak remplace fertile
          }
        }
      });
    }

    // 3. Combiner local + backend
    const combinedPeriodSet = new Set([...localPeriodSet, ...backendPeriodSet]);
    const combinedFertSet = new Set(backendFertSet);
    const combinedPeakSet = new Set(backendPeakSet);

    // 4. Calculer les cycles futurs basés sur la sélection locale (si elle existe)
    if (periodStart) {
      // Étendre la fenêtre de calcul pour couvrir plusieurs mois
      const extendedWindowStart = addDays(windowStart, -60);
      const extendedWindowEnd = addDays(windowEnd, 180);

      const { fertSet: localFertSet, peakSet: localPeakSet } = computeFertileAndPeakSets({
        lastPeriodStart: periodStart,
        cycleLength,
        windowStart: extendedWindowStart,
        windowEnd: extendedWindowEnd,
        periodSet: combinedPeriodSet,
      });

      localFertSet.forEach(k => {
        if (!combinedPeriodSet.has(k)) combinedFertSet.add(k);
      });
      
      localPeakSet.forEach(k => {
        if (!combinedPeriodSet.has(k)) {
          combinedPeakSet.add(k);
          combinedFertSet.delete(k);
        }
      });
    }

    return {
      periodSet: combinedPeriodSet,
      allFertSet: combinedFertSet,
      allPeakSet: combinedPeakSet,
    };
  }, [periodStart, periodEnd, cycleLength, cycles, predictions, windowStart, windowEnd]);

  const pillMeta = useMemo(() => computePillEdges(weeks, allFertSet, periodSet), [weeks, allFertSet, periodSet]);

  const monthTitle = useMemo(() => {
    const lng = i18n.language === "ar" ? "ar" : i18n.language === "fr" ? "fr-FR" : "en-US";
    return viewDate.toLocaleDateString(lng, { month: "long", year: "numeric" });
  }, [viewDate, i18n.language]);

  useEffect(() => {
    if (!periodStart) return;
    setPeriodEnd(addDays(periodStart, Math.max(1, periodDuration ?? 5) - 1));
  }, [periodDuration, periodStart]);

  /* ----------------- month navigation ----------------- */
  const shift = (dir) => {
    const nextView = new Date(viewDate.getFullYear(), viewDate.getMonth() + (dir === "next" ? 1 : -1), 1);
    setViewDate(nextView);
  };

  const isCellDisabled = (cellDate) => {
    const pickedDay = startOfDay(cellDate);

    // Option 1: allow all dates
    return false;

    // Option 2: block future dates only
    // return pickedDay > today;
  };

  const onPickDay = (date) => {
    const picked = startOfDay(date);
    if (!isEdit && isCellDisabled(picked)) return;

    // ✅ NOUVEAU: Chaque clic = nouveau début de cycle
    setPeriodStart(picked);
    setPeriodEnd(addDays(picked, Math.max(1, periodDuration ?? 5) - 1));
  };

  const getDayClass = (cell) => {
    let cls = "ctm-day";
    if (!cell.currentMonth) cls += " is-other";
    if (!isEdit && isCellDisabled(cell.date)) cls += " is-disabled";

    const meta = pillMeta.get(cell.key);
    if (meta?.type === "fert") cls += " is-fert";
    if (meta?.type === "per") cls += " is-per";
    if (allPeakSet.has(cell.key)) cls += " is-peak";

    if (meta?.isSingle) cls += " is-single";
    else {
      if (meta?.isStart) cls += " is-start";
      if (meta?.isEnd) cls += " is-end";
    }

    if (periodStart && ymdKey(cell.date) === ymdKey(periodStart)) cls += " is-selected";
    if (periodEnd && ymdKey(cell.date) === ymdKey(periodEnd)) cls += " is-selected";

    return cls;
  };

  /* ----------------- Sauvegarde dans le backend ----------------- */
  const handleSave = async () => {
    if (!periodStart || !userId) return;

    if (!cycleLength || !periodDuration) {
      alert(t("cycleTracker.completeInfo") || "Veuillez compléter les informations du cycle");
      return;
    }

    setIsSaving(true);

    try {
      // ✅ Créer le cycle dans le backend
      await createCycle({
        startDate: startOfDay(periodStart).toISOString(),
        endDate: startOfDay(periodEnd).toISOString(),
        cycleLengthInDays: cycleLength,
        notes: `Cycle créé - Durée moyenne: ${cycleLength} jours, Durée règles: ${periodDuration} jours`,
      });

      // ✅ Recharger les données
      await reloadAll();

      // ✅ Appeler le callback onSave si fourni
      if (onSave) {
        onSave({
          lastPeriodStart: startOfDay(periodStart).toISOString(),
          cycleLength,
          periodLength: periodDuration,
        });
      }

      // ✅ Fermer le modal
      onClose?.();
    } catch (err) {
      console.error("Erreur lors de la sauvegarde du cycle:", err);
      alert(t("cycleTracker.saveError") || "Erreur lors de la sauvegarde. Veuillez réessayer.");
    } finally {
      setIsSaving(false);
    }
  };

  const canSave =
    periodStart &&
    cycleLength != null &&
    periodDuration != null &&
    !isSaving;

  if (!open) return null;

  return (
    <div className="ctm-overlay" role="dialog" aria-modal="true" onClick={onClose}>
      <div className="ctm-modal" onClick={(e) => e.stopPropagation()}>
        <button className="ctm-x" onClick={onClose} aria-label={t("cycleTracker.close")}>
          ✕
        </button>

        <h2 className="ctm-title">{t("cycleTracker.title")}</h2>

        {/* ✅ Message de chargement ou d'erreur */}
        {isLoading && (
          <p className="ctm-loading">{t("cycleTracker.loading") || "Chargement..."}</p>
        )}
        
        {error && (
          <p className="ctm-error">{t("cycleTracker.error") || "Erreur lors du chargement des données"}</p>
        )}

        <p className="ctm-subtitle">
          {periodStart 
            ? t("cycleTracker.adjustPeriodDuration") || "Ajustez la durée de vos règles avec les contrôles ci-dessous"
            : t("cycleTracker.subtitleStart")
          }
          {!isEdit && !periodStart ? ` ${t("cycleTracker.onlyAllowedMonthsHint")}` : ""}
        </p>

        <div className="ctm-calcard">
          <header className="ctm-calhead">
            <div className="ctm-monthyear">{monthTitle}</div>

            <div className="ctm-nav">
              <button
                className="ctm-navbtn"
                type="button"
                onClick={() => shift("prev")}
                aria-label={t("cycleTracker.prevMonth")}
                disabled={false}
              >
                <img src={iconArrowL} alt="" width="12" height="12" />
              </button>

              <button
                className="ctm-navbtn"
                type="button"
                onClick={() => shift("next")}
                aria-label={t("cycleTracker.nextMonth")}
                disabled={false}
              >
                <img src={iconArrowR} alt="" width="12" height="12" />
              </button>
            </div>
          </header>

          <div className="ctm-weekdays">
            {weekdays.map((d) => (
              <div key={d} className="ctm-weekday">
                {d}
              </div>
            ))}
          </div>

          <div className="ctm-grid">
            {weeks.map((week, wi) => (
              <div className="ctm-week" key={wi}>
                {week.map((cell, ci) => (
                  <button
                    key={`${wi}-${ci}-${cell.key}`}
                    type="button"
                    className={getDayClass(cell)}
                    onClick={() => onPickDay(cell.date)}
                    aria-label={t("cycleTracker.ariaDay", { day: cell.day })}
                    disabled={!isEdit && isCellDisabled(cell.date)}
                  >
                    <span className="ctm-num">{pad2(cell.day)}</span>
                  </button>
                ))}
              </div>
            ))}
          </div>
        </div>

        <div className="ctm-controls">
          <div className="ctm-card">
            <div className="ctm-card__label">{t("cycleTracker.cycleLengthLabel")}</div>
            <div className="ctm-box">
              <div className="ctm-box__icon">
                <CycleSvg />
              </div>

              <div className="ctm-box__value">
                <div className="ctm-box__num">{cycleLength != null ? pad2(cycleLength) : "--"}</div>
                <div className="ctm-box__unit">{t("cycleTracker.daysUnit")}</div>
              </div>

              <div className="ctm-box__arrows">
                <button className="ctm-up" onClick={() => setCycleLength((v) => Math.min(60, (v ?? 28) + 1))} type="button">
                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                    <path d="M6 15L12 9L18 15" stroke="#1e1e1e" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </button>
                <button className="ctm-down" onClick={() => setCycleLength((v) => Math.max(15, (v ?? 28) - 1))} type="button">
                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                    <path d="M6 9L12 15L18 9" stroke="#1e1e1e" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </button>
              </div>
            </div>
          </div>

          <div className="ctm-card">
            <div className="ctm-card__label">{t("cycleTracker.periodDurationLabel")}</div>
            <div className="ctm-box">
              <div className="ctm-box__icon">
                <DropSvg />
              </div>

              <div className="ctm-box__value">
                <div className="ctm-box__num">{periodDuration != null ? pad2(periodDuration) : "--"}</div>
                <div className="ctm-box__unit">{t("cycleTracker.daysUnit")}</div>
              </div>

              <div className="ctm-box__arrows">
                <button className="ctm-up" onClick={() => setPeriodDuration((v) => Math.min(14, (v ?? 5) + 1))} type="button">
                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                    <path d="M6 15L12 9L18 15" stroke="#1e1e1e" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </button>
                <button className="ctm-down" onClick={() => setPeriodDuration((v) => Math.max(2, (v ?? 5) - 1))} type="button">
                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="none">
                    <path d="M6 9L12 15L18 9" stroke="#1e1e1e" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </button>
              </div>
            </div>
          </div>
        </div>

        <button
          className="ctm-save"
          type="button"
          disabled={!canSave}
          style={!canSave ? { opacity: 0.65, cursor: "not-allowed" } : undefined}
          onClick={handleSave}
        >
          {isSaving ? (t("cycleTracker.saving") || "Enregistrement...") : t("cycleTracker.saveButton")}
        </button>
      </div>
    </div>
  );
}
